import {
  MIND_ORDER_DEFAULTS,
  MindOrderCreateRequest,
  MindOrderCustomer,
  MindOrderInstallationAddress,
  MindOrderProduct,
  MindOrderSchedule,
  MindScheduleProduct,
} from '../models/mind-order.model';
import { ScheduleSlot } from '../models/schedule.model';
import { ProductV2 } from '../models/product-v2.model';
import { MindCatalogItem } from '../models/mind-catalog.model';

import {
  buildMindOrderDeal,
  MindOrderMeta,
  parseMindOrderMeta,
} from './mind-price.utils';

export function digitsOnly(value: string | null | undefined): string {
  return (value ?? '').toString().replace(/\D/g, '');
}

/** affiliateId do canal; null/undefined/blank → `''` no createOrder. */
export function resolveMindAffiliateId(
  value: string | null | undefined
): string {
  if (value == null) {
    return '';
  }
  return String(value).trim();
}


export function toScheduleProductType(type: string | null | undefined): string {
  const trimmed = (type ?? '').toString().trim();
  if (!trimmed || trimmed.toUpperCase() === 'INTERNET') {
    return 'BL';
  }
  return trimmed.toUpperCase();
}

export function toYmdDate(isoOrDate: string): string {
  if (!isoOrDate) {
    return '';
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoOrDate)) {
    return isoOrDate;
  }
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) {
    return isoOrDate.slice(0, 10);
  }
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function schedulePeriodLabel(period: number): string {
  if (period === 1) return 'Manhã';
  if (period === 2) return 'Tarde';
  return String(period);
}

export function mapScheduleSlotToOrderSchedule(
  slot: ScheduleSlot
): MindOrderSchedule {
  const dateStart = slot.proposal?.startDate ?? slot.startDate;
  const dateEnd = slot.proposal?.endDate ?? slot.endDate;
  return {
    cold: slot.cold ?? false,
    period: slot.periodTypeId,
    label: schedulePeriodLabel(slot.periodTypeId),
    date: toYmdDate(dateStart),
    dateStart,
    dateEnd,
  };
}

export function buildInstallationAddress(params: {
  zipcode: string;
  number: string;
  district?: string | null;
  complement?: string | null;
}): MindOrderInstallationAddress {
  const address: MindOrderInstallationAddress = {
    zipcode: digitsOnly(params.zipcode),
    number: (params.number ?? '').toString().trim(),
  };
  const district = (params.district ?? '').toString().trim();
  const complement = (params.complement ?? '').toString().trim();
  if (district) {
    address.district = district;
  }
  if (complement) {
    address.complement = complement;
  }
  return address;
}

function metaFromProduct(product: ProductV2): MindOrderMeta | null {
  return parseMindOrderMeta(product.customFields);
}

/** createOrder product — original DCC cents + deal/extras/tags when present. */
export function buildMindOrderProductFromCart(
  product: ProductV2
): MindOrderProduct {
  const meta = metaFromProduct(product);
  const id = meta?.productId || Number(product.uuid) || product.id;
  const type = (meta?.productType || product.sku || '').toString().trim();
  const name = meta?.productName || product.name || '';

  // Prefer stashed DCC cents; fall back to reais→cents only if meta missing (legacy cart).
  const priceCents =
    meta?.originalPriceCents ??
    Math.round((product.price ?? 0) * 100);

  const orderProduct: MindOrderProduct = {
    id,
    type,
    name,
    price: priceCents,
    hasContract: true,
    registrationFee: { ...MIND_ORDER_DEFAULTS.registrationFee },
  };

  if (meta?.extras?.length) {
    orderProduct.extras = meta.extras;
  }
  if (meta?.tags?.length) {
    orderProduct.tags = meta.tags;
  }

  if (meta?.hasPromotion && meta.deal?.periods?.[0]) {
    const period = meta.deal.periods[0];
    const promoFormatted = (period.price / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
    const originalFormatted = (priceCents / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
    orderProduct.deal = {
      description: `pague ${promoFormatted} por ${period.ate} meses, após pague ${originalFormatted}`,
      periods: [{ price: period.price, validity: period.ate * 30 }],
    };
  }

  return orderProduct;
}

export function buildMindOrderProductFromCatalogItem(
  item: MindCatalogItem
): MindOrderProduct {
  const product = item.purchasePayload?.product;
  const id = product?.id ?? Number(item.id);
  const type = (product?.type ?? item.type ?? '').toString().trim();
  const name = product?.name ?? item.displayName ?? '';
  const price =
    item.price?.priceWithDccAjustment ??
    product?.priceMonthlyCents ??
    item.price?.fullMonthlyCents ??
    item.price?.monthlyCents ??
    0;

  const orderProduct: MindOrderProduct = {
    id,
    type,
    name,
    price,
    hasContract: true,
    registrationFee: { ...MIND_ORDER_DEFAULTS.registrationFee },
  };

  const extras = item.extras ?? product?.extras;
  if (extras?.length) {
    orderProduct.extras = extras;
  }
  const tags = product?.tags ?? item.tags;
  if (tags?.length) {
    orderProduct.tags = tags.filter((t): t is string => typeof t === 'string');
  }

  const deal = buildMindOrderDeal(item);
  if (deal) {
    orderProduct.deal = deal;
  }

  return orderProduct;
}

/** Schedule API product — price in reais (display/promo), type BL coercion. */
export function buildMindScheduleProductFromCart(
  product: ProductV2
): MindScheduleProduct {
  const meta = metaFromProduct(product);
  const id = meta?.productId || Number(product.uuid) || product.id;
  const type = toScheduleProductType(meta?.productType || product.sku);
  const name = meta?.productName || product.name || '';
  const displayCents =
    meta?.displayPriceCents ?? Math.round((product.price ?? 0) * 100);

  return {
    id,
    type,
    name,
    price: displayCents / 100,
  };
}

export function buildMindCustomer(params: {
  contactName: string;
  document: string;
  companyName: string;
  email: string;
  cpf: string;
  marketingDelivery?: Partial<MindOrderCustomer['marketingDelivery']>;
}): MindOrderCustomer {
  return {
    contactName: (params.contactName ?? '').toString().trim(),
    document: digitsOnly(params.document),
    companyName: (params.companyName ?? '').toString().trim(),
    email: (params.email ?? '').toString().trim(),
    cpf: digitsOnly(params.cpf),
    marketingDelivery: {
      ...MIND_ORDER_DEFAULTS.marketingDelivery,
      ...params.marketingDelivery,
    },
  };
}

export interface BuildMindOrderCreateRequestParams {
  userUuid: string;
  regionId: string | number;
  homePass: string;
  installationAddress: MindOrderInstallationAddress;
  products: MindOrderProduct[];
  schedule: MindOrderSchedule[];
  customer: MindOrderCustomer;
  installationFee?: number;
  mediaChannel?: string;
  /** Do canal (`ChannelData.affiliateId`); vazio/nulo → `''`. */
  affiliateId?: string | null;
}

export function buildMindOrderCreateRequest(
  params: BuildMindOrderCreateRequestParams
): MindOrderCreateRequest {
  return {
    userUuid: params.userUuid,
    regionId: String(params.regionId),
    homePass: String(params.homePass),
    catalog: MIND_ORDER_DEFAULTS.catalog,
    salesChannel: MIND_ORDER_DEFAULTS.salesChannel,
    mediaChannel: params.mediaChannel ?? MIND_ORDER_DEFAULTS.mediaChannel,
    sellerCode: null,
    affiliateId: resolveMindAffiliateId(params.affiliateId),
    installationFee: params.installationFee ?? 0,
    electronicInvoicing: MIND_ORDER_DEFAULTS.electronicInvoicing,
    paymentDay: MIND_ORDER_DEFAULTS.paymentDay,
    installationAddress: params.installationAddress,
    products: params.products,
    schedule: params.schedule,
    customer: params.customer,
  };
}
